import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  User, Phone, MapPin, Calendar, AlertTriangle,
  FileText, DollarSign, Plus, Clock, Loader, ArrowLeft,
  Edit, Trash2 // Added icons
} from 'lucide-react';
import apiService from '../services/api';

const PatientProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [patient, setPatient] = useState(null);
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (id) {
      loadPatientData();
    }
  }, [id]);

  const loadPatientData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [patientData, visitsData] = await Promise.all([
        apiService.getPatient(id).catch(err => {
          console.error('Failed to load patient:', err);
          throw err;
        }),
        apiService.getPatientVisits(id).catch(err => {
          console.error('Failed to load visits:', err);
          return [];
        })
      ]);

      setPatient(patientData);
      setVisits(Array.isArray(visitsData) ? visitsData : []);

    } catch (err) {
      console.error('Failed to load patient data:', err);
      setError(err.message || 'Failed to load patient data');
    } finally {
      setLoading(false);
    }
  };

  // --- NEW: Handle Delete ---
  const handleDeleteVisit = async (visitId) => {
    if (!window.confirm("Are you sure you want to permanently delete this visit? This cannot be undone.")) {
      return;
    }

    try {
      await apiService.deleteVisit(visitId);
      // Remove the deleted visit from local state to update UI immediately
      setVisits(currentVisits => currentVisits.filter(visit => visit.id !== visitId));
      alert('Visit deleted successfully.');
    } catch (err) {
      console.error('Failed to delete visit:', err);
      alert(`Failed to delete visit: ${err.message || 'Unknown error'}`);
    }
  };

  // --- NEW: Handle Edit ---
  const handleEditVisit = (visitId) => {
    // Navigate to the edit page (You need to create this route/component)
    navigate(`/patient/${id}/visit/${visitId}/edit`);
  };

  if (loading) {
    return (
      <div className="space-y-6 px-4 sm:px-0">
        <div className="flex items-center justify-center py-12">
          <Loader className="h-8 w-8 animate-spin text-blue-600" />
          <span className="ml-2 text-gray-600">Loading patient profile...</span>
        </div>
      </div>
    );
  }

  if (error || !patient) {
    return (
      <div className="card text-center py-8 mx-4 sm:mx-0">
        <User className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          {error || 'Patient not found'}
        </h3>
        <p className="text-gray-600 mb-4">
          {error ? 'Please try again or contact support.' : 'The patient you are looking for does not exist.'}
        </p>
        <div className="flex flex-col sm:flex-row justify-center space-y-2 sm:space-y-0 sm:space-x-3">
          <Link to="/search" className="btn-primary">
            Back to Search
          </Link>
          {error && (
            <button onClick={loadPatientData} className="btn-secondary">
              Try Again
            </button>
          )}
        </div>
      </div>
    );
  }

  const totalFees = visits.reduce((sum, visit) => sum + (parseFloat(visit.total_fee) || 0), 0);

  const pendingTreatments = visits.filter(visit =>
    visit.status === 'pending' || visit.follow_up_required === 1
  );

  return (
    <div className="space-y-4 sm:space-y-6 px-4 sm:px-0">
      {/* Patient Header */}
      <div className="card">
        <div className="flex flex-col sm:flex-row sm:items-start space-y-4 sm:space-y-0 sm:space-x-6">
          <div className="flex-shrink-0 self-center sm:self-start">
            {patient.photo ? (
              <img
                src={`https://api.novadent.site/uploads/patients/${patient.photo}`}
                alt={patient.full_name}
                className="w-20 h-20 sm:w-24 sm:h-24 rounded-full object-cover border-4 border-gray-200"
              />
            ) : (
              <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-blue-600 text-white flex items-center justify-center text-xl sm:text-2xl font-bold">
                {patient.full_name?.charAt(0) || 'P'}
              </div>
            )}
          </div>

          <div className="flex-grow">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start space-y-4 sm:space-y-0">
              <div className="text-center sm:text-left">
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">{patient.full_name}</h1>
                <p className="text-base sm:text-lg text-gray-600 font-medium">Patient ID: {patient.patient_id}</p>
              </div>
              <Link
                to={`/patient/${patient.id}/visit`}
                className="btn-primary flex items-center justify-center space-x-2 w-full sm:w-auto"
              >
                <Plus className="h-4 w-4" />
                <span>New Visit</span>
              </Link>
            </div>

            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
              <div className="flex items-center text-gray-700 justify-center sm:justify-start">
                <Phone className="h-4 w-4 sm:h-5 sm:w-5 mr-2 text-blue-600 flex-shrink-0" />
                <span className="text-sm sm:text-base truncate">{patient.phone}</span>
              </div>
              <div className="flex items-center text-gray-700 justify-center sm:justify-start">
                <Calendar className="h-4 w-4 sm:h-5 sm:w-5 mr-2 text-blue-600 flex-shrink-0" />
                <span className="text-sm sm:text-base">{patient.age} years old, {patient.gender}</span>
              </div>
              <div className="flex items-center text-gray-700 justify-center sm:justify-start sm:col-span-2 lg:col-span-1">
                <MapPin className="h-4 w-4 sm:h-5 sm:w-5 mr-2 text-blue-600 flex-shrink-0" />
                <span className="text-sm sm:text-base truncate">{patient.address || 'No address provided'}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Medical Alerts */}
        {(patient.allergies || patient.chronic_conditions || patient.past_surgeries) && (
          <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center mb-3">
              <AlertTriangle className="h-5 w-5 text-red-600 mr-2" />
              <h3 className="font-semibold text-red-800">Medical Alerts</h3>
            </div>
            <div className="space-y-2 text-sm">
              {patient.allergies && patient.allergies !== 'None' && (
                <p><strong>Allergies:</strong> {patient.allergies}</p>
              )}
              {patient.chronic_conditions && patient.chronic_conditions !== 'None' && (
                <p><strong>Chronic Conditions:</strong> {patient.chronic_conditions}</p>
              )}
              {patient.past_surgeries && patient.past_surgeries !== 'None' && (
                <p><strong>Past Surgeries:</strong> {patient.past_surgeries}</p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
        <div className="card bg-blue-50">
          <div className="flex items-center">
            <div className="p-2 sm:p-3 bg-blue-600 text-white rounded-lg">
              <FileText className="h-5 w-5 sm:h-6 sm:w-6" />
            </div>
            <div className="ml-3 sm:ml-4">
              <p className="text-xs sm:text-sm font-medium text-gray-600">Total Visits</p>
              <p className="text-xl sm:text-2xl font-bold text-gray-900">{visits.length}</p>
            </div>
          </div>
        </div>

        <div className="card bg-green-50">
          <div className="flex items-center">
            <div className="p-2 sm:p-3 bg-green-600 text-white rounded-lg">
              <DollarSign className="h-5 w-5 sm:h-6 sm:w-6" />
            </div>
            <div className="ml-3 sm:ml-4">
              <p className="text-xs sm:text-sm font-medium text-gray-600">Total Fees</p>
              <p className="text-lg sm:text-2xl font-bold text-gray-900">{totalFees.toFixed(2)} MMK</p>
            </div>
          </div>
        </div>

        <div className="card bg-yellow-50">
          <div className="flex items-center">
            <div className="p-2 sm:p-3 bg-yellow-600 text-white rounded-lg">
              <Clock className="h-5 w-5 sm:h-6 sm:w-6" />
            </div>
            <div className="ml-3 sm:ml-4">
              <p className="text-xs sm:text-sm font-medium text-gray-600">Pending Treatments</p>
              <p className="text-xl sm:text-2xl font-bold text-gray-900">{pendingTreatments.length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Pending Treatments */}
      {pendingTreatments.length > 0 && (
        <div className="card">
          <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-4 flex items-center">
            <Clock className="h-5 w-5 mr-2 text-yellow-600" />
            Pending Treatments
          </h2>
          <div className="space-y-3">
            {pendingTreatments.map(visit => (
              <div key={visit.id} className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-gray-800 text-sm sm:text-base">{visit.diagnosis || visit.symptoms}</p>
                <p className="text-xs sm:text-sm text-gray-600 mt-1">
                  Visit Date: {new Date(visit.visit_date).toLocaleDateString()}
                </p>
                {visit.notes && (
                  <p className="text-xs sm:text-sm text-gray-600 mt-1">
                    Notes: {visit.notes}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Visit History */}
      <div className="card">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-6 space-y-4 sm:space-y-0">
          <h2 className="text-lg sm:text-xl font-bold text-gray-900 flex items-center">
            <FileText className="h-5 w-5 mr-2 text-blue-600" />
            Visit History
          </h2>
          <Link
            to={`/patient/${patient.id}/visit`}
            className="btn-primary flex items-center justify-center space-x-1 w-full sm:w-auto"
          >
            <Plus className="h-4 w-4" />
            <span>Add Visit</span>
          </Link>
        </div>

        {visits.length > 0 ? (
          <div className="space-y-4">
            {visits.slice().reverse().map(visit => (
              <div key={visit.id} className="visit-item border border-gray-200 rounded-lg p-4">
                <div className="flex flex-col space-y-3">
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start space-y-2 sm:space-y-0">
                    <h3 className="font-semibold text-gray-900 text-sm sm:text-base">
                      {new Date(visit.visit_date).toLocaleDateString('en-US', {
                        weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
                      })}
                    </h3>
                    
                    {/* Action Buttons */}
                    <div className="flex flex-wrap gap-2 items-center">
                      {visit.total_fee && (
                        <span className="px-2 py-1 bg-green-100 text-green-800 text-xs sm:text-sm rounded-full">
                          {parseFloat(visit.total_fee).toFixed(2)} MMK
                        </span>
                      )}
                      {visit.follow_up_required && (
                        <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs sm:text-sm rounded-full">
                          Follow-up Required
                        </span>
                      )}
                      
                      {/* Edit Button */}
                      <button 
                        onClick={() => handleEditVisit(visit.id)}
                        className="text-blue-600 hover:text-blue-800 p-1 rounded-full hover:bg-blue-50 transition duration-150"
                        title="Edit Visit"
                      >
                        <Edit className="h-4 w-4" />
                      </button>

                      {/* Delete Button */}
                      <button 
                        onClick={() => handleDeleteVisit(visit.id)}
                        className="text-red-600 hover:text-red-800 p-1 rounded-full hover:bg-red-50 transition duration-150"
                        title="Delete Visit"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-gray-600"><strong>Symptoms:</strong></p>
                      <p className="text-gray-800">{visit.symptoms || 'Not recorded'}</p>
                    </div>
                    <div>
                      <p className="text-gray-600"><strong>Diagnosis:</strong></p>
                      <p className="text-gray-800">{visit.diagnosis || 'Not recorded'}</p>
                    </div>
                  </div>

                  {visit.treatment && (
                    <div>
                      <p className="text-gray-600 text-sm"><strong>Treatment:</strong></p>
                      <p className="text-gray-800 text-sm">{visit.treatment}</p>
                    </div>
                  )}

                  {visit.prescriptions && (
                    <div>
                      <p className="text-gray-600 text-sm"><strong>Prescriptions:</strong></p>
                      <p className="text-gray-800 text-sm">{visit.prescriptions}</p>
                    </div>
                  )}

                  {visit.notes && (
                    <div>
                      <p className="text-gray-600 text-sm"><strong>Notes:</strong></p>
                      <p className="text-gray-800 text-sm">{visit.notes}</p>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No visits recorded</h3>
            <p className="text-gray-600 mb-4">Start by adding the first visit for this patient</p>
            <Link
              to={`/patient/${patient.id}/visit`}
              className="btn-primary"
            >
              Add First Visit
            </Link>
          </div>
        )}
      </div>

      <div className="flex flex-col sm:flex-row justify-between space-y-3 sm:space-y-0">
        <Link to="/search" className="btn-secondary flex items-center justify-center space-x-2">
          <ArrowLeft className="h-4 w-4" />
          <span>Back to Search</span>
        </Link>
        <Link
          to={`/patient/${patient.id}/visit`}
          className="btn-primary flex items-center justify-center"
        >
          Add New Visit →
        </Link>
      </div>
    </div>
  );
};

export default PatientProfile;